import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../../../services/realApi';
import { API_BASE_URL } from '../../../../services/config';
import { FiKey, FiShield, FiUsers, FiCopy, FiDownload, FiRefreshCw, FiAlertTriangle, FiInfo, FiChevronDown, FiChevronUp, FiUploadCloud } from 'react-icons/fi';

interface HelpTipProps {
  children: React.ReactNode;
}

const HelpTip: React.FC<HelpTipProps> = ({ children }) => (
  <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
    <FiInfo className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
    <div className="text-sm text-blue-800 dark:text-blue-200">{children}</div>
  </div>
);

interface ProviderTabsProps {
  activeProvider: string;
  setActiveProvider: (provider: string) => void;
}

const ProviderTabs: React.FC<ProviderTabsProps> = ({ activeProvider, setActiveProvider }) => {
  const providers = [
    { id: 'okta', label: 'Okta' },
    { id: 'azure', label: 'Azure AD / Entra ID' },
    { id: 'google', label: 'Google Workspace' },
    { id: 'other', label: 'Other' },
  ];

  return (
    <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
      {providers.map((p) => (
        <button
          key={p.id}
          onClick={() => setActiveProvider(p.id)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeProvider === p.id
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

interface StepItemProps {
  number: number | string;
  title: string;
  children: React.ReactNode;
}

const StepItem: React.FC<StepItemProps> = ({ number, title, children }) => (
  <div className="flex items-start space-x-3">
    <div className="flex-shrink-0 w-7 h-7 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center mt-0.5">
      <span className="text-sm font-bold text-blue-700 dark:text-blue-300">{number}</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-base font-medium text-gray-900 dark:text-white">{title}</p>
      <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{children}</div>
    </div>
  </div>
);

interface SAMLSetupGuideProps {
  provider: string;
}

const SAMLSetupGuide: React.FC<SAMLSetupGuideProps> = ({ provider }) => {
  if (provider === 'okta') {
    return (
      <div className="space-y-3">
        <StepItem number="1" title="Create a SAML app in Okta">
          <p>Go to <strong>Admin Console</strong> → <strong>Applications</strong> → <strong>Applications</strong> → <strong>Create App Integration</strong>. Select <strong>SAML 2.0</strong> and click Next. Enter <strong>"Aivilo Lens"</strong> as the app name.</p>
        </StepItem>
        <StepItem number="2" title="Enter Aivilo Lens details in Okta">
          <p>In the "Configure SAML" step, fill in:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>Single sign-on URL</strong> — your ACS URL (from the SP Metadata below)</li>
            <li><strong>Audience URI (SP Entity ID)</strong> — your SP Entity ID (from the SP Metadata below)</li>
            <li><strong>Name ID format</strong> — EmailAddress</li>
          </ul>
          <p className="mt-1">Click Next, then Finish.</p>
        </StepItem>
        <StepItem number="3" title="Copy Okta's SAML settings here">
          <p>Go to your new app's <strong>Sign On</strong> tab and click <strong>"View SAML setup instructions"</strong>. You'll find:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>Identity Provider Issuer</strong> → paste into "IdP Entity ID" below</li>
            <li><strong>Identity Provider Single Sign-On URL</strong> → paste into "IdP SSO URL" below</li>
            <li><strong>X.509 Certificate</strong> → download from Sign On tab → SAML Signing Certificates → Actions → Download. Open in a text editor, paste into "IdP Signing Certificate" below</li>
          </ul>
        </StepItem>
        <StepItem number="4" title="Assign users">
          <p>Go to the <strong>Assignments</strong> tab in Okta and assign users or groups who should have access.</p>
        </StepItem>
        <StepItem number="5" title="Enable SSO in Aivilo Lens">
          <p>After saving the configuration below, set the <strong>Login Policy</strong> to <strong>"Optional"</strong> to test SSO login. Once confirmed working, switch to "Required" to enforce SSO for all users.</p>
        </StepItem>
      </div>
    );
  }

  if (provider === 'azure') {
    return (
      <div className="space-y-3">
        <StepItem number="1" title="Create an Enterprise Application">
          <p>Go to <strong>Microsoft Entra admin center</strong> → <strong>Identity</strong> → <strong>Applications</strong> → <strong>Enterprise applications</strong> → <strong>New application</strong> → <strong>Create your own application</strong>. Enter <strong>"Aivilo Lens"</strong> as the name and select "Integrate any other application you don't find in the gallery".</p>
        </StepItem>
        <StepItem number="2" title="Enable SAML SSO">
          <p>In your new app, go to <strong>Manage</strong> → <strong>Single sign-on</strong> → select <strong>SAML</strong>.</p>
        </StepItem>
        <StepItem number="3" title="Configure Basic SAML (Section 1)">
          <p>Click <strong>Edit</strong> on "Basic SAML Configuration" and enter:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>Identifier (Entity ID)</strong> — your SP Entity ID (from SP Metadata below)</li>
            <li><strong>Reply URL (ACS URL)</strong> — your ACS URL (from SP Metadata below)</li>
          </ul>
        </StepItem>
        <StepItem number="4" title="Copy Azure's SAML settings here">
          <p>On the SAML configuration page, find <strong>Section 4 "Set up [App]"</strong>:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>Microsoft Entra Identifier</strong> → paste into "IdP Entity ID" below</li>
            <li><strong>Login URL</strong> → paste into "IdP SSO URL" below</li>
            <li>In <strong>Section 3 "SAML Signing Certificate"</strong> → Download <strong>Certificate (Base64)</strong>. Open in a text editor, paste into "IdP Signing Certificate" below</li>
          </ul>
          <p className="mt-1">Alternatively, copy the <strong>App Federation Metadata Url</strong> from Section 3 and paste it into the "IdP Metadata URL" field below for automatic configuration.</p>
        </StepItem>
        <StepItem number="5" title="Assign users">
          <p>Go to <strong>Users and groups</strong> → <strong>Add user/group</strong> to grant access.</p>
        </StepItem>
        <StepItem number="6" title="Enable SSO in Aivilo Lens">
          <p>After saving the configuration below, set the <strong>Login Policy</strong> to <strong>"Optional"</strong> to test SSO login. Once confirmed working, switch to "Required" to enforce SSO for all users.</p>
        </StepItem>
      </div>
    );
  }

  if (provider === 'google') {
    return (
      <div className="space-y-3">
        <StepItem number="1" title="Create a custom SAML app">
          <p>Go to <strong>Google Admin console</strong> → <strong>Apps</strong> → <strong>Web and mobile apps</strong> → <strong>Add App</strong> → <strong>Add custom SAML app</strong>. Enter <strong>"Aivilo Lens"</strong> as the app name and click Continue.</p>
        </StepItem>
        <StepItem number="2" title="Copy Google's IdP details">
          <p>On the <strong>"Google Identity Provider details"</strong> page you'll see:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>SSO URL</strong> → paste into "IdP SSO URL" below</li>
            <li><strong>Entity ID</strong> → paste into "IdP Entity ID" below</li>
            <li>Click <strong>Download Certificate</strong> → open in a text editor, paste into "IdP Signing Certificate" below</li>
          </ul>
          <p className="mt-1">Click Continue.</p>
        </StepItem>
        <StepItem number="3" title="Enter Aivilo Lens details in Google">
          <p>On the "Service Provider Details" page, fill in:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>ACS URL</strong> — your ACS URL (from SP Metadata below)</li>
            <li><strong>Entity ID</strong> — your SP Entity ID (from SP Metadata below)</li>
            <li><strong>Name ID format</strong> — EMAIL</li>
          </ul>
          <p className="mt-1">Click Continue, optionally add attribute mappings, then click Finish.</p>
        </StepItem>
        <StepItem number="4" title="Enable for users">
          <p>Go to your app → <strong>User access</strong> → set to <strong>"On for everyone"</strong> (or select specific organizational units). Click Save.</p>
        </StepItem>
        <StepItem number="5" title="Enable SSO in Aivilo Lens">
          <p>After saving the configuration below, set the <strong>Login Policy</strong> to <strong>"Optional"</strong> to test SSO login. Once confirmed working, switch to "Required" to enforce SSO for all users.</p>
        </StepItem>
      </div>
    );
  }

  // 'other' provider
  return (
    <div className="space-y-3">
      <StepItem number="1" title="Create a SAML 2.0 application in your IdP">
        <p>Most Identity Providers let you create a "custom SAML app" or "SAML 2.0 integration". Select that option and name it <strong>"Aivilo Lens"</strong>.</p>
      </StepItem>
      <StepItem number="2" title="Enter Aivilo Lens SP details in your IdP">
        <p>Your IdP will ask for these values (available from the SP Metadata download below):</p>
        <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
          <li><strong>ACS URL / Reply URL</strong> — the URL where your IdP sends the SAML response</li>
          <li><strong>Entity ID / Audience URI</strong> — a unique identifier for Aivilo Lens</li>
          <li><strong>Name ID format</strong> — use Email Address</li>
        </ul>
      </StepItem>
      <StepItem number="3" title="Copy your IdP's SAML settings here">
        <p>Your IdP will provide three values — paste them into the form below:</p>
        <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
          <li><strong>IdP Entity ID / Issuer</strong> — a unique identifier for your IdP</li>
          <li><strong>IdP SSO URL / Login URL</strong> — the login endpoint URL</li>
          <li><strong>X.509 Certificate</strong> — download the signing certificate from your IdP and paste it (PEM format)</li>
        </ul>
      </StepItem>
      <StepItem number="4" title="Assign users">
        <p>Assign users in your IdP who should have access to Aivilo Lens.</p>
      </StepItem>
      <StepItem number="5" title="Enable SSO in Aivilo Lens">
        <p>After saving the configuration below, set the <strong>Login Policy</strong> to <strong>"Optional"</strong> to test SSO login. Once confirmed working, switch to "Required" to enforce SSO for all users.</p>
      </StepItem>
    </div>
  );
};

interface SCIMSetupGuideProps {
  provider: string;
  scimUrl?: string;
}

const SCIMSetupGuide: React.FC<SCIMSetupGuideProps> = ({ provider, scimUrl: _scimUrl }) => {
  if (provider === 'okta') {
    return (
      <div className="space-y-3">
        <StepItem number="1" title="Enable SCIM on your Okta app">
          <p>Open your SAML app in Okta → <strong>General</strong> tab → click <strong>Edit</strong> → change <strong>Provisioning</strong> from "None" to <strong>SCIM</strong> → Save.</p>
        </StepItem>
        <StepItem number="2" title="Configure the SCIM connection">
          <p>A new <strong>Provisioning</strong> tab appears. Go to <strong>Settings</strong> → <strong>Integration</strong> → click <strong>Edit</strong>:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>SCIM connector base URL</strong> — paste the SCIM Endpoint URL from above</li>
            <li><strong>Unique identifier field</strong> — enter <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">userName</code></li>
            <li><strong>Authentication Mode</strong> — select <strong>HTTP Header</strong></li>
            <li><strong>Authorization / Bearer</strong> — paste the SCIM Bearer Token from above</li>
          </ul>
          <p className="mt-1">Click <strong>Test API Credentials</strong> to verify, then Save.</p>
        </StepItem>
        <StepItem number="3" title="Enable provisioning actions">
          <p>In the <strong>Provisioning</strong> tab → <strong>To App</strong> → click <strong>Edit</strong> → enable <strong>Create Users</strong>, <strong>Update User Attributes</strong>, and <strong>Deactivate Users</strong> → Save.</p>
        </StepItem>
        <StepItem number="4" title="Assign users">
          <p>Go to the <strong>Assignments</strong> tab → assign users or groups. Provisioning will begin automatically for assigned users.</p>
        </StepItem>
      </div>
    );
  }

  if (provider === 'azure') {
    return (
      <div className="space-y-3">
        <StepItem number="1" title="Go to Provisioning settings">
          <p>Open your Enterprise App in <strong>Microsoft Entra admin center</strong> → <strong>Manage</strong> → <strong>Provisioning</strong> → <strong>Get started</strong>.</p>
        </StepItem>
        <StepItem number="2" title="Set provisioning mode">
          <p>Set <strong>Provisioning Mode</strong> to <strong>Automatic</strong>.</p>
        </StepItem>
        <StepItem number="3" title="Enter Admin Credentials">
          <p>Under <strong>Admin Credentials</strong>:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
            <li><strong>Tenant URL</strong> — paste the SCIM Endpoint URL from above</li>
            <li><strong>Secret Token</strong> — paste the SCIM Bearer Token from above</li>
          </ul>
          <p className="mt-1">Click <strong>Test Connection</strong> to verify, then Save.</p>
        </StepItem>
        <StepItem number="4" title="Configure scope and enable">
          <p>Under <strong>Settings</strong>, set <strong>Scope</strong> to "Sync only assigned users and groups". Then set <strong>Provisioning Status</strong> to <strong>On</strong> and Save. Incremental syncs occur every 20-40 minutes.</p>
        </StepItem>
      </div>
    );
  }

  if (provider === 'google') {
    return (
      <div className="space-y-3">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start space-x-2">
            <FiAlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-base font-medium text-yellow-800 dark:text-yellow-200">SCIM is not available for custom SAML apps in Google Workspace</p>
              <p className="text-base text-yellow-700 dark:text-yellow-300 mt-1">
                Google Workspace only supports auto-provisioning (SCIM) for pre-integrated apps in the Google catalog. Custom SAML apps like Aivilo Lens cannot use Google's SCIM connector.
              </p>
              <p className="text-base text-yellow-700 dark:text-yellow-300 mt-2">
                <strong>Recommended alternative:</strong> Enable <strong>JIT (Just-In-Time) Provisioning</strong> below instead. With JIT, users are automatically created in Aivilo Lens on their first SSO login — no manual account creation needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 'other' provider
  return (
    <div className="space-y-3">
      <StepItem number="1" title="Find the SCIM / Provisioning settings in your IdP">
        <p>Look for "Provisioning", "Auto-provisioning", or "SCIM" settings in your Identity Provider's app configuration.</p>
      </StepItem>
      <StepItem number="2" title="Enter the SCIM connection details">
        <p>Your IdP will ask for:</p>
        <ul className="list-disc list-inside mt-1 space-y-0.5 ml-1">
          <li><strong>SCIM Base URL / Tenant URL / Connector URL</strong> — paste the SCIM Endpoint URL from above</li>
          <li><strong>Bearer Token / API Token / Secret Token</strong> — paste the SCIM Bearer Token from above</li>
          <li><strong>Authentication method</strong> — select Bearer Token / HTTP Header</li>
        </ul>
      </StepItem>
      <StepItem number="3" title="Enable provisioning actions">
        <p>Enable user creation, updates, and deactivation. Then assign users or groups and save.</p>
      </StepItem>
    </div>
  );
};

interface CollapsibleGuideProps {
  title: string;
  description: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleGuide: React.FC<CollapsibleGuideProps> = ({ title, description, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        </div>
        {isOpen
          ? <FiChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
          : <FiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        }
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

interface OrgScope {
  type: string;
  id?: string | null;
}

interface SSOTabProps {
  orgScope?: OrgScope;
}

const SSOTab: React.FC<SSOTabProps> = ({ orgScope } = {}) => {
  const [config, setConfig] = useState<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [scimToken, setScimToken] = useState<string | null>(null);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [activeSection, setActiveSection] = useState('saml');
  const [samlProvider, setSamlProvider] = useState('okta');
  const [scimProvider, setScimProvider] = useState('okta');

  // Form state
  const [formData, setFormData] = useState<{
    idp_entity_id: string;
    idp_sso_url: string;
    idp_slo_url: string;
    idp_certificate: string;
    idp_metadata_url: string;
    enforcement_mode: string;
    scim_enabled: boolean;
    scim_default_role: string;
    jit_provisioning_enabled: boolean;
    jit_default_role: string;
    allowed_email_domains: string[];
  }>({
    idp_entity_id: '',
    idp_sso_url: '',
    idp_slo_url: '',
    idp_certificate: '',
    idp_metadata_url: '',
    enforcement_mode: 'disabled',
    scim_enabled: false,
    scim_default_role: 'regular_user',
    jit_provisioning_enabled: false,
    jit_default_role: 'regular_user',
    allowed_email_domains: [],
  });

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setConfig(null);
      // Build endpoint with optional client_id for partner viewing client SSO
      let endpoint = '/sso/configurations';
      if (orgScope?.type === 'aws_global') {
        endpoint += '?is_aws_global=true';
      } else if (orgScope?.type === 'client' && orgScope?.id) {
        endpoint += `?client_id=${orgScope.id}`;
      }
      let configs = await apiCall(endpoint);
      if (!configs || configs.length === 0) {
        // Auto-create an empty config so SP metadata URLs are available immediately
        const createPayload: Record<string, unknown> = {};
        if (orgScope?.type === 'client' && orgScope?.id) {
          createPayload.client_id = orgScope.id;
        }
        await apiCall('/sso/configurations', {
          method: 'POST',
          body: JSON.stringify(createPayload),
        });
        configs = await apiCall(endpoint);
      }
      if ((configs?.length ?? 0) > 0) {
        const cfg = configs[0];
        setConfig(cfg);
        setFormData({
          idp_entity_id: cfg.idp_entity_id || '',
          idp_sso_url: cfg.idp_sso_url || '',
          idp_slo_url: cfg.idp_slo_url || '',
          idp_certificate: '',
          idp_metadata_url: cfg.idp_metadata_url || '',
          enforcement_mode: cfg.enforcement_mode || 'disabled',
          scim_enabled: cfg.scim_enabled || false,
          scim_default_role: cfg.scim_default_role || 'regular_user',
          jit_provisioning_enabled: cfg.jit_provisioning_enabled || false,
          jit_default_role: cfg.jit_default_role || 'regular_user',
          allowed_email_domains: cfg.allowed_email_domains || [],
        });
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '';
      if (errMsg?.includes('403') || errMsg?.includes('401')) {
        setError('You do not have permission to manage SSO settings.');
      } else {
        console.error('Error loading SSO config:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [orgScope?.type, orgScope?.id]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const payload: Record<string, unknown> = { ...formData };
      if (!payload.idp_certificate) {
        delete payload.idp_certificate;
      }

      if (config) {
        await apiCall(`/sso/configurations/${config.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setSuccessMessage('SSO configuration updated successfully.');
      } else {
        // When creating for a client via orgScope, include client_id in payload
        if (orgScope?.type === 'client' && orgScope?.id) {
          payload.client_id = orgScope.id;
        }
        await apiCall('/sso/configurations', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setSuccessMessage('SSO configuration created successfully.');
      }

      await loadConfig();
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : null) || 'Failed to save SSO configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!config) return;
    if (!globalThis.confirm('Are you sure you want to delete this SSO configuration? This will disable SSO for all users in your organization.')) {
      return;
    }
    try {
      setSaving(true);
      await apiCall(`/sso/configurations/${config.id}`, { method: 'DELETE' });
      setConfig(null);
      setFormData({
        idp_entity_id: '',
        idp_sso_url: '',
        idp_slo_url: '',
        idp_certificate: '',
        idp_metadata_url: '',
        enforcement_mode: 'disabled',
        scim_enabled: false,
        scim_default_role: 'regular_user',
        jit_provisioning_enabled: false,
        jit_default_role: 'regular_user',
        allowed_email_domains: [],
      });
      setSuccessMessage('SSO configuration deleted.');
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : null) || 'Failed to delete SSO configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateScimToken = async () => {
    if (!config) return;
    if (showTokenWarning) {
      try {
        setSaving(true);
        const result = await apiCall(`/sso/configurations/${config.id}/generate-scim-token`, {
          method: 'POST',
        });
        setScimToken(result.token);
        setShowTokenWarning(false);
        setSuccessMessage('SCIM token generated. Copy it now — it will not be shown again.');
        await loadConfig();
      } catch (err: unknown) {
        setError((err instanceof Error ? err.message : null) || 'Failed to generate SCIM token.');
      } finally {
        setSaving(false);
      }
    } else {
      setShowTokenWarning(true);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard.');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error === 'You do not have permission to manage SSO settings.') {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
        <p className="text-yellow-800 dark:text-yellow-200">{error}</p>
      </div>
    );
  }

  const enforcementOptions = [
    { value: 'disabled', label: 'Disabled', description: 'SSO is not active. Users log in with their email and password as usual.' },
    { value: 'optional', label: 'Optional', description: 'Users can choose to log in with SSO or with their password. Good for testing SSO before enforcing it.' },
    { value: 'required', label: 'Required', description: 'Password login is blocked. All users must log in through SSO. Enable only after testing.' },
  ];

  const roleOptions = [
    { value: 'regular_user', label: 'Regular User' },
    { value: 'admin_user', label: 'Admin User' },
    { value: 'account_owner', label: 'Account Owner' },
  ];

  const sectionTabs = [
    { id: 'saml', label: 'SAML SSO', icon: FiKey, description: 'Single Sign-On login' },
    { id: 'scim', label: 'User Provisioning', icon: FiRefreshCw, description: 'Automatic user sync (optional)' },
  ];

  let saveButtonLabel: string;
  if (saving) {
    saveButtonLabel = 'Saving...';
  } else if (config) {
    saveButtonLabel = 'Update Configuration';
  } else {
    saveButtonLabel = 'Save Configuration';
  }

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-base text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}
      {error && error !== 'You do not have permission to manage SSO settings.' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-base text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {sectionTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                activeSection === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{tab.label}</span>
              <span className="hidden sm:inline text-sm opacity-60">— {tab.description}</span>
            </button>
          );
        })}
      </div>

      {/* ==================== SAML SSO Section ==================== */}
      {activeSection === 'saml' && (
        <div className="space-y-6">
          {/* Setup Guide - Collapsible, provider-tabbed */}
          <CollapsibleGuide
            title="Setup Guide — How to configure SAML SSO"
            description="Step-by-step instructions for your Identity Provider"
            defaultOpen={!config}
          >
            <ProviderTabs activeProvider={samlProvider} setActiveProvider={setSamlProvider} />
            <SAMLSetupGuide provider={samlProvider} />
          </CollapsibleGuide>

          {/* SSO Status Card + SAML Config */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <FiKey className="w-7 h-7 text-blue-500" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">SAML Configuration</h3>
              </div>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                config
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                {config ? 'Configured' : 'Not Configured'}
              </span>
            </div>

            <p className="text-base text-gray-500 dark:text-gray-400 mb-5">
              Enter the SAML settings from your Identity Provider. See the setup guide above for where to find each value.
            </p>

            {/* IdP Configuration */}
            <div className="space-y-4">
              <div>
                <label htmlFor="sso-idp-metadata-url" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                  IdP Metadata URL <span className="text-sm font-normal text-gray-400">(recommended — auto-fills the fields below)</span>
                </label>
                <input
                  id="sso-idp-metadata-url"
                  type="url"
                  value={formData.idp_metadata_url}
                  onChange={(e) => setFormData({ ...formData, idp_metadata_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base"
                  placeholder="e.g. https://login.microsoftonline.com/.../federationmetadata/2007-06/..."
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  If your IdP provides a metadata URL (Azure AD does; Okta and Google usually don't), paste it here. Otherwise, fill in the fields below manually.
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white dark:bg-gray-800 px-3 text-sm text-gray-400">or configure manually</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sso-idp-entity-id" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                    IdP Entity ID <span className="text-sm font-normal text-gray-400">(Issuer)</span>
                  </label>
                  <input
                    id="sso-idp-entity-id"
                    type="text"
                    value={formData.idp_entity_id}
                    onChange={(e) => setFormData({ ...formData, idp_entity_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="e.g. http://www.okta.com/exk1234567890"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Called "Issuer" in Okta, "Microsoft Entra Identifier" in Azure AD, or "Entity ID" in Google.
                  </p>
                </div>
                <div>
                  <label htmlFor="sso-idp-sso-url" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                    IdP SSO URL <span className="text-sm font-normal text-gray-400">(Login URL)</span>
                  </label>
                  <input
                    id="sso-idp-sso-url"
                    type="url"
                    value={formData.idp_sso_url}
                    onChange={(e) => setFormData({ ...formData, idp_sso_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="e.g. https://yourcompany.okta.com/app/xxxx/sso/saml"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Called "Identity Provider Single Sign-On URL" in Okta, "Login URL" in Azure AD, or "SSO URL" in Google.
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="sso-idp-certificate" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                  IdP Signing Certificate <span className="text-sm font-normal text-gray-400">(X.509, PEM format)</span>
                </label>
                <textarea
                  id="sso-idp-certificate"
                  value={formData.idp_certificate}
                  onChange={(e) => setFormData({ ...formData, idp_certificate: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base font-mono"
                  placeholder={config ? '(certificate stored — paste new to replace)' : '-----BEGIN CERTIFICATE-----\nMIIC8DCCAdig...\n-----END CERTIFICATE-----'}
                />
                <div className="mt-2">
                  <label className="inline-flex items-center space-x-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer text-sm transition-colors">
                    <FiUploadCloud className="w-4 h-4" />
                    <span>Upload .cer / .pem file</span>
                    <input
                      type="file"
                      accept=".cer,.pem,.crt,.cert,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (evt) => {
                          const result = evt.target?.result;
                          setFormData({ ...formData, idp_certificate: typeof result === 'string' ? result : '' });
                        };
                        reader.readAsText(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                <HelpTip>
                  <p className="font-medium mb-1">Where do I get this certificate?</p>
                  <p>
                    Your Identity Provider generates this certificate — you do <strong>not</strong> need to create one yourself.
                    Download it from your IdP and paste it here:
                  </p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li><strong>Okta:</strong> Sign On tab → SAML Signing Certificates → Actions → Download certificate</li>
                    <li><strong>Azure AD:</strong> SAML configuration page → Section 3 "SAML Signing Certificate" → Download "Certificate (Base64)"</li>
                    <li><strong>Google:</strong> SAML setup wizard step 2 → click "Download Certificate"</li>
                  </ul>
                  <p className="mt-1">Open the downloaded <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">.cer</code> or <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">.pem</code> file in a text editor and paste the entire content here, including the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">-----BEGIN CERTIFICATE-----</code> and <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">-----END CERTIFICATE-----</code> lines.</p>
                </HelpTip>
              </div>
            </div>
          </div>

          {/* SP Details for IdP */}
          {config && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3 mb-2">
                <FiDownload className="w-6 h-6 text-blue-500" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Aivilo Lens SP Details (give these to your IdP)</h3>
              </div>
              <p className="text-base text-gray-500 dark:text-gray-400 mb-4">
                Your Identity Provider needs these values to complete the SAML setup. Copy and paste them into your IdP's SAML configuration.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="sso-acs-url" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                    ACS URL <span className="text-sm font-normal text-gray-400">(Assertion Consumer Service URL / Reply URL / Single sign-on URL)</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="sso-acs-url"
                      type="text"
                      readOnly
                      value={`${API_BASE_URL}/auth/sso/acs/${config.id}`}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white text-base font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(`${API_BASE_URL}/auth/sso/acs/${config.id}`)}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      title="Copy to clipboard"
                    >
                      <FiCopy className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="sso-sp-entity-id" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                    SP Entity ID <span className="text-sm font-normal text-gray-400">(Audience URI / Identifier)</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="sso-sp-entity-id"
                      type="text"
                      readOnly
                      value={`${API_BASE_URL}/auth/sso/metadata/${config.id}`}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white text-base font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(`${API_BASE_URL}/auth/sso/metadata/${config.id}`)}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      title="Copy to clipboard"
                    >
                      <FiCopy className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Some IdPs accept a metadata XML file instead. You can download it here:
                  </p>
                  <a
                    href={`${API_BASE_URL}/auth/sso/metadata/${config.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
                  >
                    <FiDownload className="w-4 h-4" />
                    <span>Download SP Metadata XML</span>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Enforcement Mode */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-2">
              <FiShield className="w-6 h-6 text-purple-500" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Login Policy</h3>
            </div>
            <p className="text-base text-gray-500 dark:text-gray-400 mb-4">
              Choose how users in your organization log in. Start with "Optional" to test, then switch to "Required" once confirmed working.
            </p>
            <div className="space-y-3">
              {enforcementOptions.map((opt) => (
                <label key={opt.value} aria-label={opt.label} className={`flex items-start space-x-3 cursor-pointer p-3 rounded-lg border transition-colors ${
                  formData.enforcement_mode === opt.value
                    ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}>
                  <input
                    type="radio"
                    name="enforcement_mode"
                    value={opt.value}
                    checked={formData.enforcement_mode === opt.value}
                    onChange={(e) => setFormData({ ...formData, enforcement_mode: e.target.value })}
                    className="mt-0.5 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-base font-medium text-gray-900 dark:text-white">{opt.label}</span>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{opt.description}</p>
                  </div>
                </label>
              ))}
              {formData.enforcement_mode === 'required' && (
                <div className="mt-2 flex items-start space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <FiAlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Important:</strong> When set to "Required", all users in your organization will be unable to log in with passwords.
                    Make sure SSO is fully configured and tested with "Optional" mode first. Verify that at least one admin can log in via SSO before enabling this.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Allowed Email Domains */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-2">
              <FiShield className="w-6 h-6 text-blue-500" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Allowed Email Domains</h3>
            </div>
            <p className="text-base text-gray-500 dark:text-gray-400 mb-3">
              Specify which email domains are allowed to use this SSO configuration. This is required for new users logging in via SSO for the first time (JIT provisioning).
              Separate multiple domains with commas.
            </p>
            <div>
              <input
                type="text"
                placeholder="e.g. aivilo.ai, example.com"
                value={(formData.allowed_email_domains || []).join(', ')}
                onChange={(e) => {
                  const domains = e.target.value
                    .split(',')
                    .map(d => d.trim().toLowerCase())
                    .filter(d => d);
                  setFormData({ ...formData, allowed_email_domains: domains.length > 0 ? domains : [] });
                }}
                className="w-full max-w-lg px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base"
              />
            </div>
          </div>

          {/* JIT Provisioning */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <FiUsers className="w-6 h-6 text-green-500" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Just-in-Time User Creation</h3>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle just-in-time user creation">
                <input
                  type="checkbox"
                  checked={formData.jit_provisioning_enabled}
                  onChange={(e) => setFormData({ ...formData, jit_provisioning_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-500 peer-checked:bg-blue-600" />
              </label>
            </div>
            <p className="text-base text-gray-500 dark:text-gray-400 mb-3">
              When enabled, if someone logs in via SSO but doesn't have an Aivilo Lens account yet, one will be created automatically with the role below.
              If disabled, users must be created manually in the Users tab before they can log in via SSO.
            </p>
            {formData.jit_provisioning_enabled && (
              <div>
                <label htmlFor="sso-jit-default-role" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Role for Auto-Created Users
                </label>
                <select
                                                                                  id="sso-jit-default-role"
                  value={formData.jit_default_role}
                  onChange={(e) => setFormData({ ...formData, jit_default_role: e.target.value })}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base"
                >
                  {roleOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Save / Delete buttons for SAML section */}
          <div className="flex items-center justify-between">
            <div>
              {config && (
                <button
                  onClick={handleDeleteConfig}
                  disabled={saving}
                  className="px-4 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-base disabled:opacity-50"
                >
                  Delete SSO Configuration
                </button>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-base font-medium disabled:opacity-50"
            >
              {saveButtonLabel}
            </button>
          </div>
        </div>
      )}

      {/* ==================== SCIM / User Provisioning Section ==================== */}
      {activeSection === 'scim' && (
        <div className="space-y-6">
          {/* SCIM Intro */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">What is User Provisioning (SCIM)?</h3>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-3">
              SCIM automatically syncs user accounts between your Identity Provider and Aivilo Lens.
              When you add or remove someone in your IdP (Okta, Azure AD, Google Workspace), the change is automatically reflected here — no manual user management needed.
            </p>
            <div className="flex items-start space-x-2 p-3 bg-white/60 dark:bg-gray-800/60 rounded-md">
              <FiInfo className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p className="font-medium text-gray-800 dark:text-gray-200 text-base mb-1">SCIM is optional and separate from SSO</p>
                <p>
                  Most organizations only need <strong>SAML SSO</strong> (configured in the previous tab) to enable single sign-on.
                  SCIM is an additional feature for larger organizations that want to automate user account creation and deactivation.
                  You do <strong>not</strong> need SCIM to use SSO — you do <strong>not</strong> need a certificate for SCIM.
                </p>
              </div>
            </div>
          </div>

          {config ? (
            <>
              {/* SCIM Enable Toggle */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <FiRefreshCw className="w-6 h-6 text-orange-500" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">SCIM Provisioning</h3>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle SCIM provisioning">
                    <input
                      type="checkbox"
                      checked={formData.scim_enabled}
                      onChange={(e) => setFormData({ ...formData, scim_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-500 peer-checked:bg-blue-600" />
                  </label>
                </div>
                <p className="text-base text-gray-500 dark:text-gray-400">
                  Enable this to allow your Identity Provider to automatically create, update, and deactivate user accounts in Aivilo Lens.
                </p>
              </div>

              {formData.scim_enabled && (
                <>
                  {/* SCIM Setup Guide - Collapsible, provider-tabbed */}
                  <CollapsibleGuide
                    title="Setup Guide — How to configure SCIM in your IdP"
                    description="Step-by-step instructions for connecting your Identity Provider"
                    defaultOpen={true}
                  >
                    <ProviderTabs activeProvider={scimProvider} setActiveProvider={setScimProvider} />
                    <SCIMSetupGuide
                      provider={scimProvider}
                      scimUrl={`${API_BASE_URL}/scim/v2/${config.id}`}
                    />
                  </CollapsibleGuide>

                  {/* SCIM Connection Details */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Connection Details</h4>
                    <p className="text-base text-gray-500 dark:text-gray-400 mb-4">
                      Copy these two values and paste them into your Identity Provider's SCIM provisioning settings (see setup guide above).
                    </p>

                    {/* SCIM Endpoint URL */}
                    <div className="mb-5">
                      <label htmlFor="sso-scim-endpoint" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SCIM Endpoint URL
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        Called "SCIM connector base URL" in Okta, "Tenant URL" in Azure AD, or "Endpoint URL" in Google.
                      </p>
                      <div className="flex items-center space-x-2">
                        <input
                                                                                  id="sso-scim-endpoint"
                          type="text"
                          readOnly
                          value={`${API_BASE_URL}/scim/v2/${config.id}`}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white text-base font-mono"
                        />
                        <button
                          onClick={() => copyToClipboard(`${API_BASE_URL}/scim/v2/${config.id}`)}
                          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          title="Copy to clipboard"
                        >
                          <FiCopy className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* SCIM Bearer Token */}
                    <div className="mb-5">
                      <label htmlFor="sso-scim-token" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SCIM Bearer Token
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        Called "Authorization / Bearer" in Okta, "Secret Token" in Azure AD, or "Access Token" in Google.
                        This is like a password that authorizes your IdP to manage users in Aivilo Lens.
                      </p>
                      {scimToken ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                                                                                  id="sso-scim-token"
                              type="text"
                              readOnly
                              value={scimToken}
                              className="flex-1 px-3 py-2 border border-green-300 dark:border-green-600 rounded-md bg-green-50 dark:bg-green-900/20 text-gray-900 dark:text-white text-base font-mono"
                            />
                            <button
                              onClick={() => copyToClipboard(scimToken)}
                              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              title="Copy to clipboard"
                            >
                              <FiCopy className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="flex items-start space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                            <FiAlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-yellow-800 dark:text-yellow-200">
                              <strong>Copy this token now.</strong> It will not be shown again after you leave this page. Store it securely.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {showTokenWarning ? (
                            <div className="space-y-2">
                              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                  {config.scim_bearer_token_hash
                                    ? 'Generating a new token will invalidate the previous one. Any existing SCIM integration using the old token will stop working until you update it in your IdP.'
                                    : 'A new SCIM token will be generated. You will need to copy it and paste it into your Identity Provider.'}
                                </p>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleGenerateScimToken}
                                  disabled={saving}
                                  className="px-3 py-1.5 bg-orange-600 text-white text-base rounded-md hover:bg-orange-700 disabled:opacity-50"
                                >
                                  {saving ? 'Generating...' : 'Confirm Generate'}
                                </button>
                                <button
                                  onClick={() => setShowTokenWarning(false)}
                                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-base rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={handleGenerateScimToken}
                              disabled={saving}
                              className="px-4 py-2 bg-blue-600 text-white text-base rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                              {config.scim_bearer_token_hash ? 'Regenerate SCIM Token' : 'Generate SCIM Token'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Default Role */}
                    <div>
                      <label htmlFor="sso-scim-default-role" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Default Role for SCIM-Provisioned Users
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        When your IdP creates a new user via SCIM, they will be assigned this role. You can change individual roles later in the Users tab.
                      </p>
                      <select
                                                                                  id="sso-scim-default-role"
                        value={formData.scim_default_role}
                        onChange={(e) => setFormData({ ...formData, scim_default_role: e.target.value })}
                        className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-base"
                      >
                        {roleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Save button for SCIM changes */}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-base font-medium disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
              <FiAlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-base text-yellow-800 dark:text-yellow-200 font-medium">Set up SAML SSO first</p>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                SCIM provisioning requires an active SSO configuration. Go to the "SAML SSO" tab to set up SSO first.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SSOTab;
